// Wishlist functions
//
// Pure client-side wishlist (no app / no customer metafields): liked products
// are stored as {id, handle} pairs in localStorage, keyed by product id.
// The /pages/wishlist listing page (sections/wishlist-page.liquid) reads this
// same localStorage key and re-fetches each product via /products/{handle}.js
// — see renderWishlistPage() below.

var WISHLIST_STORAGE_KEY = 'wishlist_products';

function getWishlist() {
    try {
        var raw = localStorage.getItem(WISHLIST_STORAGE_KEY);
        var parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
}

function saveWishlist(list) {
    try {
        localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
        // storage unavailable (private mode, quota, etc.) — fail silently
    }
}

function isInWishlist(productId) {
    return getWishlist().some(function (item) {
        return String(item.id) === String(productId);
    });
}

function addToWishlist(productId, productHandle) {
    if (!productId || isInWishlist(productId)) return;
    var list = getWishlist();
    list.push({ id: String(productId), handle: productHandle || '' });
    saveWishlist(list);
    refreshWishlistUI();
}

function removeFromWishlist(productId) {
    var list = getWishlist().filter(function (item) {
        return String(item.id) !== String(productId);
    });
    saveWishlist(list);
    refreshWishlistUI();
}

// Central refresh: keeps every add-to-wishlist button, the header badge, and
// (if present) the /pages/wishlist listing grid all in sync after any change.
function refreshWishlistUI() {
    setWishlistButtonsState();
    updateWishlistCount();
    if (document.querySelector('[data-wishlist-grid]')) renderWishlistPage();
}

function toggleWishlist(btn) {
    var productId = btn.dataset.productId;
    var productHandle = btn.dataset.productHandle;
    if (!productId) return;

    if (isInWishlist(productId)) {
        removeFromWishlist(productId);
    } else {
        addToWishlist(productId, productHandle);
    }
}

// Sync every .add-to-wishlist button's active/pressed state with localStorage.
// Called on load and after every add/remove so all instances of a product
// (gallery, recommendation cards, etc.) stay in sync with each other.
function setWishlistButtonsState() {
    document.querySelectorAll('.add-to-wishlist').forEach(function (btn) {
        var active = isInWishlist(btn.dataset.productId);
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
}

function updateWishlistCount() {
    var count = getWishlist().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (badge) {
        badge.textContent = count;
        badge.hidden = count === 0;
    });
}

document.addEventListener('DOMContentLoaded', function () {
    setWishlistButtonsState();
    updateWishlistCount();
    if (document.querySelector('[data-wishlist-grid]')) renderWishlistPage();
});

// -----------------------------------------------------------------------
// /pages/wishlist listing grid
// -----------------------------------------------------------------------
// There's no real Shopify collection backing this list (it's per-browser
// localStorage), so we re-fetch each liked product individually via the
// Ajax API (/products/{handle}.js) and build the cards client-side.

function buildWishlistCard(product, currencyCode) { 
    var variant =
        (product.variants || []).find(function (v) {
            return v.available;
        }) || (product.variants || [])[0];

    var image = product.featured_image || (product.images && product.images[0]) || '';
    if (image && image.indexOf('width=') === -1) {
        image += (image.indexOf('?') === -1 ? '?' : '&') + 'width=600';
    }

    var centsPrice = (variant ? variant.price : product.price);
    var realPrice = 'AED ' + (parseInt(centsPrice, 10) || 0) / 100;


    var card = document.createElement('div');
    card.className = 'wishlist-page__item';
    card.innerHTML = `
        <a href="${product.url}" class="wishlist-page__media">
          <img class="wishlist-page__image" src="${image}" alt="${escapeWishlistHtml(product.title)}" width="224" height="280" loading="lazy">
        </a>
        <a href="${product.url}" class="wishlist-page__title-link">
          <span class="wishlist-page__product-title">${escapeWishlistHtml(product.title)}</span>
        </a>
        <div class="wishlist-page__meta">
          <p data-price="${realPrice}" class="price-holder-value">${realPrice}</p>
          <a href="${product.url}" class="wishlist-page__details">
            View Details
            <span>→</span>
          </a>
        </div>
        <button type="button" class="wishlist-page__remove" aria-label="Remove from Wishlist" onclick="removeFromWishlist('${product.id}')">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="#666" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
    `;

    return card;
}

function escapeWishlistHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
}

function setWishlistPageLoading(isLoading) {
    var page = document.querySelector('[data-wishlist-page]');
    var loader = document.querySelector('[data-wishlist-loading]');
    if (page) page.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    if (loader) loader.hidden = !isLoading;
}

async function renderWishlistPage() {
    var grid = document.querySelector('[data-wishlist-grid]');
    var emptyState = document.querySelector('[data-wishlist-empty]');
    if (!grid) return;

    var list = getWishlist();
    var currencyCode = grid.dataset.currency;
    var showSpinner = grid.childElementCount === 0;

    if (!list.length) {
        grid.innerHTML = '';
        setWishlistPageLoading(false);
        if (emptyState) emptyState.hidden = false;
        return;
    }

    if (emptyState) emptyState.hidden = true;
    if (showSpinner) setWishlistPageLoading(true);

    try {
        var results = await Promise.all(
            list.map(function (item) {
                if (!item.handle) return Promise.resolve({ item: item, product: null });
                return fetch('/products/' + item.handle + '.js')
                    .then(function (res) {
                        return res.ok ? res.json() : null;
                    })
                    .catch(function () {
                        return null;
                    })
                    .then(function (product) {
                        return { item: item, product: product };
                    });
            })
        );

        // Drop stale entries (product deleted/unpublished/handle changed) so the
        // wishlist doesn't accumulate dead references.
        var stale = results.filter(function (r) {
            return !r.product;
        });
        if (stale.length) {
            var staleIds = stale.map(function (r) {
                return String(r.item.id);
            });
            saveWishlist(
                getWishlist().filter(function (item) {
                    return staleIds.indexOf(String(item.id)) === -1;
                })
            );
            setWishlistButtonsState();
            updateWishlistCount();
        }

        var products = results
            .map(function (r) {
                return r.product;
            })
            .filter(Boolean);

        if (!products.length) {
            grid.innerHTML = '';
            if (emptyState) emptyState.hidden = false;
            return;
        }

        grid.innerHTML = '';
        products.forEach(function (product) {
            grid.appendChild(buildWishlistCard(product, currencyCode));
        });
        if (typeof runCurrencyConverter === 'function') {
            runCurrencyConverter();
        }
    } finally {
        setWishlistPageLoading(false);
    }
}
